import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import { API_ENDPOINTS } from '../config.js';
import { validatePassword, checkPasswordRequirements } from '../utils/passwordValidation';

export default function Signup() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    pib: '',
    mb: '',
    company: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordRequirements, setPasswordRequirements] = useState(null);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Check if user already has a valid session (cookie-based)
  useEffect(() => {
    // Check if user has valid session by calling profile endpoint
    fetch(`${API_ENDPOINTS.PROFILE}`, {
      method: 'GET',
      credentials: 'include', // Include cookies
    })
      .then(res => {
        if (res.ok) {
          // User has valid session, redirect to dashboard
          navigate('/dashboard', { replace: true });
        }
      })
      .catch(() => {
        // No valid session, stay on signup page
      });
  }, [navigate]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setError('');
    setSuccess('');
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Real-time password validation
    if (name === 'password') {
      setShowPasswordRequirements(true);
      const requirements = checkPasswordRequirements(value);
      setPasswordRequirements(requirements);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.email || !form.password || !form.confirmPassword || !form.pib || !form.mb || !form.company) {
      setError(t('signup.allFieldsRequired'));
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError(t('signup.passwordsDoNotMatch'));
      return;
    }
    
    // Validate password strength
    const passwordValidation = validatePassword(form.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors.join(' '));
      return;
    }
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_ENDPOINTS.AUTH_REGISTER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Include cookies for CSRF token
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          pib: form.pib,
          mb: form.mb,
          company: form.company,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Handle field-level errors
        if (data.fields && Object.keys(data.fields).length > 0) {
          setFieldErrors(data.fields);
          setError(data.error || t('signup.registrationError'));
        } else {
          setFieldErrors({});
          setError(data.error || t('signup.registrationError'));
        }
        setSuccess('');
      } else {
        setSuccess(data.message || t('signup.registrationSuccess'));
        setError('');
        setFieldErrors({});
        setTimeout(() => {
          navigate('/login');
        }, 4000);
      }
    } catch (err) {
      setError(t('signup.serverError'));
      setSuccess('');
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 10px',
    margin: '6px 0 18px 0',
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    fontSize: 16,
    background: colors.backgroundHover,
    color: colors.text,
    outline: 'none',
    transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
  };

  return (
    <div style={{minHeight: '100vh', background: colors.backgroundLight, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.3s ease'}}>
      <form onSubmit={handleSubmit} style={{background: colors.card, borderRadius: 16, boxShadow: `0 4px 24px ${colors.shadowHover}`, padding: 36, width: '100%', maxWidth: 400, transition: 'background-color 0.3s ease, box-shadow 0.3s ease'}}>
        <h2 style={{fontSize: 28, fontWeight: 800, color: colors.primary, marginBottom: 24}}>{t('signup.title')}</h2>
        {error && <div style={{color: colors.error, marginBottom: 12}}>{error}</div>}
        {success && <div style={{color: colors.success, marginBottom: 12}}>{success}</div>}
        <label style={{fontWeight: 600, color: colors.text}}>{t('auth.email')}</label>
        <input 
          type="email" 
          name="email" 
          value={form.email} 
          onChange={handleChange} 
          style={{
            ...inputStyle,
            borderColor: fieldErrors.email ? colors.error : colors.border
          }} 
          autoComplete="email" 
          required 
        />
        {fieldErrors.email && (
          <div style={{color: colors.error, fontSize: '12px', marginTop: '-12px', marginBottom: '12px'}}>
            {fieldErrors.email}
          </div>
        )}
        <label style={{fontWeight: 600, color: colors.text}}>{t('auth.password')}</label>
        <input 
          type="password" 
          name="password" 
          value={form.password} 
          onChange={handleChange} 
          onFocus={() => setShowPasswordRequirements(true)}
          style={{
            ...inputStyle,
            borderColor: fieldErrors.password ? colors.error : colors.border
          }} 
          autoComplete="new-password" 
          required 
        />
        {fieldErrors.password && (
          <div style={{color: colors.error, fontSize: '12px', marginTop: '-12px', marginBottom: '12px'}}>
            {fieldErrors.password}
          </div>
        )}
        {showPasswordRequirements && form.password && (
          <div style={{
            marginTop: '-12px',
            marginBottom: '12px',
            fontSize: '12px',
            color: colors.textSecondary,
            background: colors.backgroundLight,
            padding: '12px',
            borderRadius: '6px',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{ fontWeight: 600, marginBottom: '6px', color: colors.text }}>{t('profile.passwordRequirements')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ color: passwordRequirements?.length ? colors.success : colors.error }}>
                {passwordRequirements?.length ? '✓' : '✗'} {t('profile.minLength')}
              </div>
              <div style={{ color: passwordRequirements?.uppercase ? colors.success : colors.error }}>
                {passwordRequirements?.uppercase ? '✓' : '✗'} {t('profile.uppercase')}
              </div>
              <div style={{ color: passwordRequirements?.lowercase ? colors.success : colors.error }}>
                {passwordRequirements?.lowercase ? '✓' : '✗'} {t('profile.lowercase')}
              </div>
              <div style={{ color: passwordRequirements?.number ? colors.success : colors.error }}>
                {passwordRequirements?.number ? '✓' : '✗'} {t('profile.number')}
              </div>
              <div style={{ color: passwordRequirements?.special ? colors.success : colors.error }}>
                {passwordRequirements?.special ? '✓' : '✗'} {t('profile.special')}
              </div>
            </div>
          </div>
        )}
        <label style={{fontWeight: 600, color: colors.text}}>{t('signup.confirmPassword')}</label>
        <input 
          type="password" 
          name="confirmPassword" 
          value={form.confirmPassword} 
          onChange={handleChange} 
          style={{
            ...inputStyle,
            borderColor: fieldErrors.confirmPassword ? colors.error : colors.border
          }} 
          autoComplete="new-password" 
          required 
        />
        {fieldErrors.confirmPassword && (
          <div style={{color: colors.error, fontSize: '12px', marginTop: '-12px', marginBottom: '12px'}}>
            {fieldErrors.confirmPassword}
          </div>
        )}
        <label style={{fontWeight: 600, color: colors.text}}>{t('signup.pib')}</label>
        <input 
          type="text" 
          name="pib" 
          value={form.pib} 
          onChange={handleChange} 
          style={{
            ...inputStyle,
            borderColor: fieldErrors.pib ? colors.error : colors.border
          }} 
          required 
        />
        {fieldErrors.pib && (
          <div style={{color: colors.error, fontSize: '12px', marginTop: '-12px', marginBottom: '12px'}}>
            {fieldErrors.pib}
          </div>
        )}
        <label style={{fontWeight: 600, color: colors.text}}>{t('signup.mb')}</label>
        <input 
          type="text" 
          name="mb" 
          value={form.mb} 
          onChange={handleChange} 
          style={{
            ...inputStyle,
            borderColor: fieldErrors.mb ? colors.error : colors.border
          }} 
          required 
        />
        {fieldErrors.mb && (
          <div style={{color: colors.error, fontSize: '12px', marginTop: '-12px', marginBottom: '12px'}}>
            {fieldErrors.mb}
          </div>
        )}
        <label style={{fontWeight: 600, color: colors.text}}>{t('signup.company')}</label>
        <input 
          type="text" 
          name="company" 
          value={form.company} 
          onChange={handleChange} 
          style={{
            ...inputStyle,
            borderColor: fieldErrors.company ? colors.error : colors.border
          }} 
          required 
        />
        {fieldErrors.company && (
          <div style={{color: colors.error, fontSize: '12px', marginTop: '-12px', marginBottom: '12px'}}>
            {fieldErrors.company}
          </div>
        )}
        <button type="submit" style={{marginTop: 18, width: '100%', background: colors.primary, color: 'white', fontWeight: 700, fontSize: 18, borderRadius: 8, padding: '14px 0', border: 'none', boxShadow: `0 2px 8px ${colors.shadow}`, cursor: 'pointer', transition: 'background-color 0.2s'}} onMouseEnter={(e) => e.target.style.background = colors.primaryHover} onMouseLeave={(e) => e.target.style.background = colors.primary}>{t('auth.signup')}</button>
        <div style={{ marginTop: 18, textAlign: 'center', fontSize: 15 }}>
      {t('auth.alreadyHaveAccount')} <a href="/login" style={{ color: colors.primary, fontWeight: 600, textDecoration: 'none' }}>{t('auth.login')}</a>
    </div>
      </form>

    </div>
  );
} 